import {
  Document,
  Paragraph,
  TextRun,
  Packer,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  HeadingLevel,
  AlignmentType,
  ShadingType,
  PageBreak,
  TabStopType,
  LeaderType,
} from 'docx';
import { saveAs } from 'file-saver';
import {
  calculateSPRSScore,
  calculateSOC2Score,
  calculateISO27001Score,
  calculateHIPAAScore,
  calculateNISTCSFScore,
  calculatePCIDSSScore,
  calculateFedRAMPScore,
} from './data/controlWeights';

// ── Page geometry (US Letter) ─────────────────────────────────────────────────
// Width: 12240 DXA, Height: 15840 DXA
// Margins: 1440 each side => Content width = 12240 - 2880 = 9360 DXA
const CONTENT_WIDTH = 9360;

// ── Color constants ───────────────────────────────────────────────────────────
const NAVY       = '0F3460';
const ACCENT     = '2D7DD2';
const GREEN      = '1DB87A';
const AMBER      = 'F5A623';
const RED        = 'E05252';
const LIGHT_GRAY = 'F3F4F6';
const WHITE      = 'FFFFFF';
const DARK_TEXT  = '1A1A2E';
const MUTED      = '6B7280';
const BORDER     = 'D1D5DB';

// Suppress unused-var lint warnings — all constants used below
void GREEN; void AMBER; void RED;

// ── Framework metadata ────────────────────────────────────────────────────────
const CMMC_FRAMEWORKS = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171'];

const FRAMEWORK_META = {
  cmmc_l1:  { subtitle: 'CMMC 2.0 — Level 1 Foundational',                   classification: 'CUI',              shortName: 'CMMC L1'   },
  cmmc_l2:  { subtitle: 'CMMC 2.0 — Level 2 Advanced',                       classification: 'CUI',              shortName: 'CMMC L2'   },
  cmmc_l3:  { subtitle: 'CMMC 2.0 — Level 3 Expert',                         classification: 'CUI',              shortName: 'CMMC L3'   },
  nist_171: { subtitle: 'Controlled Unclassified Information Assessment',     classification: 'CUI',              shortName: 'NIST 171'  },
  soc2:     { subtitle: 'Trust Services Criteria Assessment',                 classification: 'Confidential',     shortName: 'SOC 2'     },
  iso27001: { subtitle: 'ISO/IEC 27001:2022 Annex A Control Assessment',     classification: 'Confidential',     shortName: 'ISO 27001' },
  hipaa:    { subtitle: 'HIPAA Security Rule Safeguard Assessment',           classification: 'PHI - Restricted', shortName: 'HIPAA'     },
  nist_csf: { subtitle: 'Cybersecurity Framework Maturity Evaluation',        classification: 'Internal',         shortName: 'NIST CSF'  },
  pci_dss:  { subtitle: 'Payment Card Industry Data Security Assessment',     classification: 'Confidential',     shortName: 'PCI DSS'   },
  fedramp:  { subtitle: 'NIST SP 800-53 Rev 5 Control Assessment',            classification: 'FedRAMP',          shortName: 'FedRAMP'   },
};

const PCT_SCORE_FNS = {
  soc2:     calculateSOC2Score,
  iso27001: calculateISO27001Score,
  hipaa:    calculateHIPAAScore,
  nist_csf: calculateNISTCSFScore,
  pci_dss:  calculatePCIDSSScore,
  fedramp:  calculateFedRAMPScore,
};

function getMeta(framework) {
  return FRAMEWORK_META[framework] || {
    subtitle: 'Control Assessment', classification: 'Confidential',
    shortName: (framework || 'REPORT').toUpperCase(),
  };
}

// ── Score helpers ─────────────────────────────────────────────────────────────
function computePctScore(framework, answers, questions) {
  const fn = PCT_SCORE_FNS[framework];
  if (fn) return fn(answers);
  const yes   = questions.filter(q => answers[q.id] === 'yes').length;
  const total = questions.length;
  return total > 0 ? Math.round((yes / total) * 100) : 0;
}

function computeDomainStats(questions, answers) {
  const map = {};
  questions.forEach(q => {
    if (!map[q.domain]) map[q.domain] = { id: q.domain, name: q.domain, met: 0, total: 0 };
    map[q.domain].total++;
    if (answers[q.id] === 'yes') map[q.domain].met++;
  });
  return Object.values(map).map(d => ({
    ...d,
    pct: d.total > 0 ? Math.round((d.met / d.total) * 100) : 0,
  }));
}

function getNistRef(controlId) {
  if (!controlId) return '';
  const m = controlId.match(/(\d+\.\d+\.\d+)/);
  return m ? m[1] : '';
}

function domainStatusLabel(pct) {
  return pct >= 80 ? 'On Track' : pct >= 50 ? 'In Progress' : 'At Risk';
}

// ── Color helpers ─────────────────────────────────────────────────────────────
function statusColors(status) {
  const s = (status || '').toLowerCase();
  if (s === 'implemented' || s === 'compliant' || s === 'yes' || s === 'on track' || s === 'current' || s === 'approved')
    return { bg: 'D1FAE5', text: '065F46' };
  if (s === 'partial' || s === 'partially compliant' || s === 'in progress' || s === 'expiring soon')
    return { bg: 'FEF3C7', text: '92400E' };
  if (s === 'not implemented' || s === 'non-compliant' || s === 'no' || s === 'at risk' || s === 'open' || s === 'expired')
    return { bg: 'FEE2E2', text: '991B1B' };
  if (s === 'not answered' || s === 'draft')
    return { bg: 'F3F4F6', text: '6B7280' };
  return { bg: WHITE, text: DARK_TEXT };
}

function priorityColors(priority) {
  const p = (priority || '').toUpperCase().replace('MEDIUM', 'MED');
  if (p === 'HIGH') return { bg: 'FEE2E2', text: '991B1B' };
  if (p === 'MED')  return { bg: 'FEF3C7', text: '92400E' };
  if (p === 'LOW')  return { bg: 'D1FAE5', text: '065F46' };
  return { bg: WHITE, text: DARK_TEXT };
}

function scoreTextColor(pct) {
  if (pct >= 80) return '065F46';
  if (pct >= 50) return '92400E';
  return '991B1B';
}

// ── Text helper ───────────────────────────────────────────────────────────────
function trunc(text, maxLen) {
  if (!text) return 'Not specified';
  const s = String(text);
  return s.length > maxLen ? s.substring(0, maxLen) + '...' : s;
}

function fmtDate(dateStr) {
  if (!dateStr) return 'Not scheduled';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (e) {
    return String(dateStr);
  }
}

// ── Border presets ────────────────────────────────────────────────────────────
const CELL_BORDERS = {
  top:    { style: BorderStyle.SINGLE, size: 1, color: BORDER },
  bottom: { style: BorderStyle.SINGLE, size: 1, color: BORDER },
  left:   { style: BorderStyle.SINGLE, size: 1, color: BORDER },
  right:  { style: BorderStyle.SINGLE, size: 1, color: BORDER },
};

const NO_BORDER = { style: BorderStyle.NONE, size: 0, color: WHITE };
const NO_CELL_BORDERS = { top: NO_BORDER, bottom: NO_BORDER, left: NO_BORDER, right: NO_BORDER };
const NO_TABLE_BORDERS = {
  top: NO_BORDER, bottom: NO_BORDER,
  left: NO_BORDER, right: NO_BORDER,
  insideH: NO_BORDER, insideV: NO_BORDER,
};

// ── Cell helpers ──────────────────────────────────────────────────────────────
function styledCell(text, options = {}) {
  const {
    bold    = false,
    color   = DARK_TEXT,
    bgColor = WHITE,
    width   = 4680,
    fontSize = 18,
    italic  = false,
    align   = null,
    borders = CELL_BORDERS,
  } = options;
  return new TableCell({
    width:   { size: width, type: WidthType.DXA },
    shading: { fill: bgColor, type: ShadingType.CLEAR, color: 'auto' },
    margins: { top: 80, bottom: 80, left: 120, right: 120 },
    borders,
    children: [new Paragraph({
      ...(align ? { alignment: align } : {}),
      children: [new TextRun({
        text:    String(text || ''),
        bold,
        color,
        size:    fontSize,
        italics: italic,
        font:    'Calibri',
      })],
      spacing: { after: 0 },
    })],
  });
}

function headerCell(text, width = 4680) {
  return styledCell(text, {
    bold:    true,
    color:   WHITE,
    bgColor: NAVY,
    width,
    fontSize: 18,
  });
}

function labelCell(text, width = 2880) {
  return styledCell(text, {
    bold:    true,
    color:   NAVY,
    bgColor: LIGHT_GRAY,
    width,
    fontSize: 18,
  });
}

function valueCell(text, width = 6480) {
  return styledCell(text, {
    bold:    false,
    color:   DARK_TEXT,
    bgColor: WHITE,
    width,
    fontSize: 18,
  });
}

function statusCell(status, width = 1440) {
  const { bg, text } = statusColors(status);
  return styledCell(status, {
    bold:    true,
    color:   text,
    bgColor: bg,
    width,
    fontSize: 18,
    align:   AlignmentType.CENTER,
  });
}

function altBg(i) {
  return i % 2 === 0 ? WHITE : LIGHT_GRAY;
}

// ── Paragraph helpers ─────────────────────────────────────────────────────────
function para(text, options = {}) {
  const {
    bold          = false,
    size          = 20,
    color         = DARK_TEXT,
    align         = null,
    italic        = false,
    spacingAfter  = 160,
    spacingBefore = 0,
  } = options;
  return new Paragraph({
    ...(align ? { alignment: align } : {}),
    children: [new TextRun({ text: String(text || ''), bold, size, color, italics: italic, font: 'Calibri' })],
    spacing: { after: spacingAfter, before: spacingBefore },
  });
}

function blankLine(space = 120) {
  return new Paragraph({ children: [new TextRun({ text: '' })], spacing: { after: space } });
}

function pageBreak() {
  return new Paragraph({ children: [new PageBreak()] });
}

function dividerLine(color = ACCENT) {
  return new Paragraph({
    border: { bottom: { style: BorderStyle.SINGLE, size: 6, color, space: 1 } },
    spacing: { before: 240, after: 240 },
    children: [new TextRun({ text: '' })],
  });
}

// ── Two-column info table ──────────────────────────────────────────────────────
function infoTable(rows, labelWidth = 2880, valueWidth = 6480) {
  return new Table({
    columnWidths: [labelWidth, valueWidth],
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    rows: rows.map(([lbl, val]) => new TableRow({
      children: [labelCell(lbl, labelWidth), valueCell(String(val || 'Not specified'), valueWidth)],
    })),
  });
}

// ── Main export ───────────────────────────────────────────────────────────────
export async function generateSSPDocx({
  level,
  levelData,
  answers,
  questions,
  tasks,
  evidence,
  profile,
  framework     = 'cmmc_l2',
  company       = {},
  narratives    = [],
  controlHistory = [],
  sprsHistory   = [],
}) {
  const today    = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const meta     = getMeta(framework);
  const isCmmc   = CMMC_FRAMEWORKS.includes(framework);
  const orgName  = company?.name || profile?.company_name || 'Organization Name';
  const cageCode = company?.cage_code || profile?.cage_code || '';

  const pctScore  = computePctScore(framework, answers, questions);
  const sprsScore = isCmmc ? calculateSPRSScore(answers) : null;
  const metCount  = questions.filter(q => answers[q.id] === 'yes').length;
  const partialCount = questions.filter(q => answers[q.id] === 'partial').length;
  const totalQs   = questions.length;
  const gapCount  = questions.filter(q => answers[q.id] === 'no').length;
  const notAnsweredCount = questions.filter(q => !answers[q.id] || answers[q.id] === 'not answered').length;
  const openTasks = (tasks || []).filter(t => !t.done);

  const domains = isCmmc && levelData?.domains
    ? levelData.domains
    : computeDomainStats(questions, answers);

  const highPri = openTasks.filter(t => (t.priority || '').toLowerCase() === 'high').length;
  const dueSoon = openTasks.filter(t => {
    if (!t.due) return false;
    const d = new Date(t.due);
    return !isNaN(d.getTime()) && (d - new Date()) / 86400000 <= 30;
  }).length;

  const expiredEv = (evidence || []).filter(e => {
    if (!e.expires_at) return false;
    return (new Date(e.expires_at) - new Date()) < 0;
  }).length;
  const expiringSoonEv = (evidence || []).filter(e => {
    if (!e.expires_at) return false;
    const d = (new Date(e.expires_at) - new Date()) / 86400000;
    return d >= 0 && d <= 30;
  }).length;

  const scoreDisplay = isCmmc
    ? `SPRS score of ${sprsScore} out of 110`
    : `compliance score of ${pctScore}%`;

  const postureStatement = pctScore >= 80
    ? 'The organization demonstrates a strong compliance posture.'
    : pctScore >= 50
      ? 'The organization is making measurable progress toward full compliance.'
      : 'Significant remediation is required to achieve full compliance.';

  const execSummaryText =
    `${orgName} has completed a ${meta.shortName} assessment as of ${today}. ` +
    `The organization has implemented ${metCount} of ${totalQs} controls, achieving a ${scoreDisplay}. ` +
    (isCmmc ? `The current SPRS score is ${sprsScore} out of 110. ` : '') +
    `${gapCount} gap${gapCount !== 1 ? 's' : ''} have been identified and are tracked in the Plan of Action and Milestones. ` +
    postureStatement;

  const children = [];

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 1 — COVER PAGE
  // ════════════════════════════════════════════════════════════════════════════

  // Wordmark
  children.push(
    para('TheClearAudit', { bold: true, size: 24, color: NAVY, align: AlignmentType.LEFT, spacingAfter: 120 }),
    para('SYSTEM SECURITY PLAN', { size: 13, color: MUTED, align: AlignmentType.LEFT, spacingAfter: 480 }),
  );

  // Title
  children.push(
    para('System Security Plan', { bold: true, size: 40, color: NAVY, align: AlignmentType.LEFT, spacingAfter: 120 }),
    para(meta.subtitle, { size: 20, color: ACCENT, align: AlignmentType.LEFT, spacingAfter: 480 }),
  );

  // Divider
  children.push(dividerLine(NAVY));

  // Company
  children.push(para(orgName, { bold: true, size: 28, color: NAVY, align: AlignmentType.LEFT, spacingAfter: 120 }));
  if (company?.system_name) {
    children.push(para(company.system_name, { size: 16, color: MUTED, align: AlignmentType.LEFT, spacingAfter: 480 }));
  } else {
    children.push(blankLine(480));
  }

  // Score block — borderless 2-column table
  const scoreNum = isCmmc ? String(sprsScore) : `${pctScore}%`;
  const scoreLabel = isCmmc ? 'SPRS Score' : 'Compliance Score';

  children.push(
    new Table({
      columnWidths: [3000, 6360],
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      borders: NO_TABLE_BORDERS,
      rows: [new TableRow({
        children: [
          // Left: big score
          new TableCell({
            width: { size: 3000, type: WidthType.DXA },
            borders: NO_CELL_BORDERS,
            margins: { top: 0, bottom: 0, left: 0, right: 240 },
            children: [
              new Paragraph({
                children: [new TextRun({ text: scoreNum, bold: true, size: 96, color: ACCENT, font: 'Calibri' })],
                spacing: { after: 40 },
              }),
              new Paragraph({
                children: [new TextRun({ text: scoreLabel, size: 16, color: MUTED, font: 'Calibri' })],
                spacing: { after: 0 },
              }),
            ],
          }),
          // Right: stats
          new TableCell({
            width: { size: 6360, type: WidthType.DXA },
            borders: NO_CELL_BORDERS,
            margins: { top: 120, bottom: 0, left: 240, right: 0 },
            children: [
              new Paragraph({ children: [new TextRun({ text: `Controls Implemented: ${metCount} of ${totalQs}`, size: 18, color: DARK_TEXT, font: 'Calibri' })], spacing: { after: 80 } }),
              new Paragraph({ children: [new TextRun({ text: `Open Gaps: ${gapCount}`, size: 18, color: DARK_TEXT, font: 'Calibri' })], spacing: { after: 80 } }),
              new Paragraph({ children: [new TextRun({ text: `Assessment Date: ${today}`, size: 18, color: DARK_TEXT, font: 'Calibri' })], spacing: { after: 0 } }),
            ],
          }),
        ],
      })],
    }),
    blankLine(320),
  );

  // Document control table
  const docCtrlRows = [
    ['Prepared By',      company?.reviewed_by       || profile?.contact_name || 'Not specified'],
    ['Reviewed By',      company?.reviewed_by        || 'Pending review'],
    ['Approved By',      company?.approved_by        || 'Pending approval'],
    ['Date Prepared',    today],
    ['Document Version', company?.document_version  || '1.0'],
    ['Next Review Date', company?.next_review_date ? fmtDate(company.next_review_date) : 'Not scheduled'],
    ['Classification',   meta.classification],
  ];

  children.push(
    new Table({
      columnWidths: [3120, 6240],
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      rows: [
        // Header spanning 2 cols
        new TableRow({
          children: [new TableCell({
            columnSpan: 2,
            width: { size: CONTENT_WIDTH, type: WidthType.DXA },
            shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            borders: CELL_BORDERS,
            children: [new Paragraph({
              children: [new TextRun({ text: 'Document Control', bold: true, size: 20, color: WHITE, font: 'Calibri' })],
              spacing: { after: 0 },
            })],
          })],
        }),
        ...docCtrlRows.map(([lbl, val]) => new TableRow({
          children: [labelCell(lbl, 3120), valueCell(val, 6240)],
        })),
      ],
    }),
    blankLine(320),
  );

  // Executive summary
  children.push(
    para('Executive Summary', { bold: true, size: 20, color: NAVY, spacingAfter: 100 }),
    para(execSummaryText, { size: 18, color: DARK_TEXT, align: AlignmentType.BOTH, spacingAfter: 200 }),
    pageBreak(),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 2 — TABLE OF CONTENTS
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Table of Contents', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 280 } }),
    blankLine(80),
  );

  const tocItems = [
    ['1', 'System Description',               3],
    ['2', 'Responsible Parties',               4],
    ['3', 'Domain Readiness Summary',          5],
    ['4', 'Assessment Results',                6],
    ['5', 'Plan of Action and Milestones',     7],
    ['6', 'Evidence Inventory',                8],
    ['7', 'Audit Trail',                       9],
    ...(isCmmc ? [['8', 'SPRS Score History', 10]] : []),
    ['\u2014', 'Signatures and Certification', isCmmc ? 11 : 10],
  ];

  tocItems.forEach(([num, title, pg]) => {
    children.push(
      new Paragraph({
        tabStops: [{ type: TabStopType.RIGHT, position: 8640, leader: LeaderType.DOT }],
        children: [
          new TextRun({ text: num === '\u2014' ? `    ${title}` : `${num}. ${title}`, font: 'Calibri', size: 20, color: DARK_TEXT }),
          new TextRun({ text: `\t${pg}`, font: 'Calibri', size: 20, color: MUTED }),
        ],
        spacing: { before: 120, after: 120 },
      }),
    );
  });

  children.push(blankLine(200), pageBreak());

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 3 — CONFIDENTIALITY NOTICE
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Confidentiality and Handling Notice', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 200 } }),
    para(
      'This System Security Plan contains Controlled Unclassified Information (CUI) as defined by Executive Order 13556 and 32 CFR Part 2002. This document is intended solely for authorized personnel involved in the security assessment, authorization, and oversight of the described information system.',
      { size: 18, align: AlignmentType.BOTH, spacingAfter: 160 },
    ),
    para(
      'Unauthorized disclosure, reproduction, or distribution of this document or its contents is strictly prohibited. Recipients are responsible for protecting this information in accordance with applicable laws, regulations, and organizational policies.',
      { size: 18, align: AlignmentType.BOTH, spacingAfter: 160 },
    ),
    para(
      'This document shall be stored, transmitted, and disposed of in accordance with CUI handling requirements as specified in NIST SP 800-171 and applicable agency policies.',
      { size: 18, align: AlignmentType.BOTH, spacingAfter: 240 },
    ),
  );

  // Classification box
  children.push(
    new Table({
      columnWidths: [CONTENT_WIDTH],
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      rows: [new TableRow({
        children: [new TableCell({
          width: { size: CONTENT_WIDTH, type: WidthType.DXA },
          shading: { fill: 'FEF3C7', type: ShadingType.CLEAR, color: 'auto' },
          margins: { top: 160, bottom: 160, left: 240, right: 240 },
          borders: {
            top:    { style: BorderStyle.SINGLE, size: 4, color: AMBER },
            bottom: { style: BorderStyle.SINGLE, size: 4, color: AMBER },
            left:   { style: BorderStyle.SINGLE, size: 4, color: AMBER },
            right:  { style: BorderStyle.SINGLE, size: 4, color: AMBER },
          },
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: `CLASSIFICATION: ${meta.classification}`, bold: true, size: 20, color: '92400E', font: 'Calibri' })],
            spacing: { after: 0 },
          })],
        })],
      })],
    }),
    blankLine(200),
    pageBreak(),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 4 — SYSTEM DESCRIPTION
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 1 \u2014 System Description', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 200 } }),
  );

  const sec1Rows = [
    ['Organization Name',     orgName],
    ['System Name',           company?.system_name           || 'Not specified'],
    ['System Type',           company?.system_type           || 'Not specified'],
    ['Operating Environment', company?.operating_environment || 'Not specified'],
    ...(isCmmc ? [['CAGE Code', cageCode || 'Not set']] : []),
    ['Assessment Date',       today],
    ['Document Version',      company?.document_version      || '1.0'],
    ['Classification',        meta.classification],
    ['Framework',             meta.shortName],
    ['Next Review Date',      company?.next_review_date ? fmtDate(company.next_review_date) : 'Not scheduled'],
    ...(isCmmc ? [['CMMC Target Level', `Level ${level}`], ['SPRS Score', `${sprsScore} out of 110`]] : [['Compliance Score', `${pctScore}%`]]),
    ['Controls Implemented',  `${metCount} of ${totalQs}`],
    ['Open Gaps',             String(gapCount)],
  ];

  children.push(infoTable(sec1Rows, 2880, 6480), blankLine(240));

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'System Boundary and Scope', bold: true, size: 24, color: ACCENT, font: 'Calibri' })], spacing: { before: 240, after: 160 } }),
  );

  if (company?.system_boundary) {
    children.push(para(company.system_boundary, { size: 18, align: AlignmentType.BOTH, spacingAfter: 160 }));
  } else {
    children.push(para('No system boundary description has been provided. To add this narrative, go to Company Settings and complete the System Security Plan section.', { size: 18, color: MUTED, italic: true, spacingAfter: 160 }));
  }

  children.push(blankLine(120), pageBreak());

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 5 — RESPONSIBLE PARTIES
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 2 \u2014 Responsible Parties', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 200 } }),
  );

  // 4 cols: 2160 + 2160 + 2160 + 2880 = 9360
  const partiesColWidths = [2160, 2160, 2160, 2880];
  const partiesRows = [
    ['System Owner',         company?.system_owner_name    || 'Not specified', company?.system_owner_title   || '',   'Responsible for overall system and operations'],
    ['ISSO',                 company?.isso_name            || 'Not specified', company?.isso_email           || '',   'Day-to-day security operations'],
    ['Authorizing Official', company?.authorizing_official || 'Not specified', '',                                    'Authorizes system operation'],
    ['Primary Contact',      profile?.contact_name         || 'Not specified', profile?.phone                || '',   'Prepared this SSP'],
    ['Reviewed By',          company?.reviewed_by          || 'Not specified', '',                                    'Reviewed this SSP'],
    ['Approved By',          company?.approved_by          || 'Not specified', '',                                    'Approved this SSP'],
  ];

  children.push(
    new Table({
      columnWidths: partiesColWidths,
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      rows: [
        new TableRow({
          children: ['Role', 'Name', 'Contact', 'Responsibility'].map((h, i) => headerCell(h, partiesColWidths[i])),
          tableHeader: true,
        }),
        ...partiesRows.map(([role, name, contact, resp], i) => new TableRow({
          children: [
            styledCell(role,    { bold: true,  color: NAVY,      bgColor: altBg(i), width: partiesColWidths[0], fontSize: 18 }),
            styledCell(name,    { bold: false, color: DARK_TEXT, bgColor: altBg(i), width: partiesColWidths[1], fontSize: 18 }),
            styledCell(contact, { bold: false, color: MUTED,     bgColor: altBg(i), width: partiesColWidths[2], fontSize: 18 }),
            styledCell(resp,    { bold: false, color: DARK_TEXT, bgColor: altBg(i), width: partiesColWidths[3], fontSize: 18 }),
          ],
        })),
      ],
    }),
    blankLine(120),
    pageBreak(),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 6 — DOMAIN READINESS SUMMARY
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 3 \u2014 Domain Readiness Summary', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 200 } }),
  );

  // 4 cols: 3240 + 1800 + 2160 + 2160 = 9360
  const domainColWidths = [3240, 1800, 2160, 2160];

  children.push(
    new Table({
      columnWidths: domainColWidths,
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      rows: [
        new TableRow({
          children: ['Domain / Category', 'Score', 'Controls Met', 'Status'].map((h, i) => headerCell(h, domainColWidths[i])),
          tableHeader: true,
        }),
        ...domains.map((d, i) => {
          const lbl    = domainStatusLabel(d.pct);
          const sColor = scoreTextColor(d.pct);
          return new TableRow({
            children: [
              styledCell(d.name,               { color: DARK_TEXT, bgColor: altBg(i), width: domainColWidths[0], fontSize: 18 }),
              styledCell(`${d.pct}%`,          { bold: true, color: sColor, bgColor: altBg(i), width: domainColWidths[1], fontSize: 18 }),
              styledCell(`${d.met} / ${d.total}`, { color: DARK_TEXT, bgColor: altBg(i), width: domainColWidths[2], fontSize: 18 }),
              statusCell(lbl, domainColWidths[3]),
            ],
          });
        }),
      ],
    }),
    blankLine(120),
    pageBreak(),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 7 — ASSESSMENT RESULTS
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 4 \u2014 Assessment Results', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 160 } }),
    para(
      `Total controls: ${totalQs}  |  Implemented: ${metCount}  |  Partial: ${partialCount}  |  Not Implemented: ${gapCount}  |  Not Answered: ${notAnsweredCount}`,
      { size: 16, color: MUTED, spacingAfter: 240 },
    ),
  );

  // CMMC: 6 cols = 1440+1440+1080+2520+1440+1440 = 9360
  // non-CMMC: 5 cols = 1440+1440+2880+1800+1800 = 9360
  const assessColsCmmc    = [1440, 1440, 1080, 2520, 1440, 1440];
  const assessColsNonCmmc = [1440, 1440, 2880, 1800, 1800];
  const assessCols = isCmmc ? assessColsCmmc : assessColsNonCmmc;

  const assessHeaderNames = isCmmc
    ? ['Control ID', 'NIST Ref', 'Domain', 'Requirement', 'Status', 'Narrative']
    : ['Control ID', 'Domain', 'Requirement', 'Status', 'Narrative'];

  const assessDataRows = questions.map((q, i) => {
    const ans       = answers[q.id] || 'not answered';
    const label     = ans === 'yes' ? 'Implemented' : ans === 'partial' ? 'Partial' : ans === 'no' ? 'Not Implemented' : 'Not Answered';
    const narrative = (narratives || []).find(n => n.control_id === q.id)?.narrative || '';
    const narDisplay = narrative ? trunc(narrative, 60) : '—';
    const rowBg     = altBg(i);
    const { bg: sBg, text: sFg } = statusColors(label);

    const statusCellNode = new TableCell({
      width:   { size: isCmmc ? assessCols[4] : assessCols[3], type: WidthType.DXA },
      shading: { fill: sBg, type: ShadingType.CLEAR, color: 'auto' },
      margins: { top: 80, bottom: 80, left: 120, right: 120 },
      borders: CELL_BORDERS,
      children: [new Paragraph({
        alignment: AlignmentType.CENTER,
        children:  [new TextRun({ text: label, bold: true, size: 16, color: sFg, font: 'Calibri' })],
        spacing:   { after: 0 },
      })],
    });

    if (isCmmc) {
      return new TableRow({ children: [
        styledCell(q.id,           { bold: true, color: NAVY,      bgColor: rowBg, width: assessCols[0], fontSize: 16 }),
        styledCell(getNistRef(q.id),{ color: MUTED,     bgColor: rowBg, width: assessCols[1], fontSize: 16 }),
        styledCell(q.domain,       { color: MUTED,     bgColor: rowBg, width: assessCols[2], fontSize: 16 }),
        styledCell(trunc(q.text, 70), { color: DARK_TEXT, bgColor: rowBg, width: assessCols[3], fontSize: 16 }),
        statusCellNode,
        styledCell(narDisplay,     { color: MUTED, italic: narDisplay !== '—', bgColor: rowBg, width: assessCols[5], fontSize: 14 }),
      ]});
    }
    return new TableRow({ children: [
      styledCell(q.id,              { bold: true, color: NAVY,      bgColor: rowBg, width: assessCols[0], fontSize: 16 }),
      styledCell(q.domain,          { color: MUTED,     bgColor: rowBg, width: assessCols[1], fontSize: 16 }),
      styledCell(trunc(q.text, 70), { color: DARK_TEXT, bgColor: rowBg, width: assessCols[2], fontSize: 16 }),
      statusCellNode,
      styledCell(narDisplay,        { color: MUTED, italic: narDisplay !== '—', bgColor: rowBg, width: assessCols[4], fontSize: 14 }),
    ]});
  });

  children.push(
    new Table({
      columnWidths: assessCols,
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      rows: [
        new TableRow({
          children: assessHeaderNames.map((h, i) => headerCell(h, assessCols[i])),
          tableHeader: true,
        }),
        ...assessDataRows,
      ],
    }),
    blankLine(120),
    pageBreak(),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 8 — PLAN OF ACTION AND MILESTONES
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 5 \u2014 Plan of Action and Milestones', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 160 } }),
    para(`Total open items: ${openTasks.length}  |  High priority: ${highPri}  |  Due within 30 days: ${dueSoon}`, { size: 16, color: MUTED, spacingAfter: 240 }),
  );

  // 7 cols: 1080+1440+2160+900+1440+1080+1260 = 9360
  const poamCols = [1080, 1440, 2160, 900, 1440, 1080, 1260];

  const poamDataRows = openTasks.map((t, i) => {
    const itemId   = `POA&M-${String(i + 1).padStart(3, '0')}`;
    const priority = (t.priority || 'low').toUpperCase().replace('MEDIUM', 'MED');
    const { bg: pBg, text: pFg } = priorityColors(priority);
    const rowBg = altBg(i);

    const dueDateDisplay = t.due ? (() => {
      try { return new Date(t.due).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
      catch (e) { return String(t.due); }
    })() : '—';

    return new TableRow({ children: [
      styledCell(itemId,               { bold: true, color: NAVY,      bgColor: rowBg, width: poamCols[0], fontSize: 16 }),
      styledCell(t.domain  || '—',     { color: MUTED,     bgColor: rowBg, width: poamCols[1], fontSize: 16 }),
      styledCell(trunc(t.name || t.title || '', 40), { color: DARK_TEXT, bgColor: rowBg, width: poamCols[2], fontSize: 16 }),
      new TableCell({
        width:   { size: poamCols[3], type: WidthType.DXA },
        shading: { fill: pBg, type: ShadingType.CLEAR, color: 'auto' },
        margins: { top: 80, bottom: 80, left: 120, right: 120 },
        borders: CELL_BORDERS,
        children: [new Paragraph({
          alignment: AlignmentType.CENTER,
          children:  [new TextRun({ text: priority, bold: true, size: 16, color: pFg, font: 'Calibri' })],
          spacing:   { after: 0 },
        })],
      }),
      styledCell(trunc(t.owner || '', 20), { color: DARK_TEXT, bgColor: rowBg, width: poamCols[4], fontSize: 16 }),
      styledCell(dueDateDisplay,       { color: DARK_TEXT, bgColor: rowBg, width: poamCols[5], fontSize: 16 }),
      statusCell('Open', poamCols[6]),
    ]});
  });

  children.push(
    new Table({
      columnWidths: poamCols,
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      rows: [
        new TableRow({ children: ['Item ID', 'Control', 'Weakness', 'Priority', 'Owner', 'Due Date', 'Status'].map((h, i) => headerCell(h, poamCols[i])), tableHeader: true }),
        ...(poamDataRows.length > 0 ? poamDataRows : [
          new TableRow({ children: [new TableCell({
            columnSpan: 7,
            width: { size: CONTENT_WIDTH, type: WidthType.DXA },
            borders: CELL_BORDERS,
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: 'No open action items.', size: 18, color: MUTED, italics: true, font: 'Calibri' })], spacing: { after: 0 } })],
          })] }),
        ]),
      ],
    }),
    blankLine(120),
    pageBreak(),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 9 — EVIDENCE INVENTORY
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 6 \u2014 Evidence Inventory', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 160 } }),
    para(`Total files: ${(evidence || []).length}  |  Expired: ${expiredEv}  |  Expiring within 30 days: ${expiringSoonEv}`, { size: 16, color: MUTED, spacingAfter: 240 }),
  );

  // 5 cols: 2520+1800+1440+1800+1800 = 9360
  const evCols = [2520, 1800, 1440, 1800, 1800];

  const evDataRows = (evidence || []).map((e, i) => {
    let expiryDisplay = 'None';
    let evStatus = e.status || 'Draft';
    if (e.expires_at) {
      const daysLeft = Math.floor((new Date(e.expires_at) - new Date()) / 86400000);
      expiryDisplay = fmtDate(e.expires_at);
      if (daysLeft < 0)       evStatus = 'Expired';
      else if (daysLeft <= 30) evStatus = 'Expiring Soon';
      else evStatus = 'Current';
    }
    const rowBg = altBg(i);
    const { bg: evBg, text: evFg } = statusColors(evStatus);

    return new TableRow({ children: [
      styledCell(e.name    || '—',    { bold: true, color: DARK_TEXT, bgColor: rowBg, width: evCols[0], fontSize: 16 }),
      styledCell(e.control || '—',    { color: MUTED,    bgColor: rowBg, width: evCols[1], fontSize: 16 }),
      styledCell(e.date    || '—',    { color: MUTED,    bgColor: rowBg, width: evCols[2], fontSize: 16 }),
      styledCell(expiryDisplay,       { color: MUTED,    bgColor: rowBg, width: evCols[3], fontSize: 16 }),
      new TableCell({
        width:   { size: evCols[4], type: WidthType.DXA },
        shading: { fill: evBg, type: ShadingType.CLEAR, color: 'auto' },
        margins: { top: 80, bottom: 80, left: 120, right: 120 },
        borders: CELL_BORDERS,
        children: [new Paragraph({
          alignment: AlignmentType.CENTER,
          children:  [new TextRun({ text: evStatus, bold: true, size: 16, color: evFg, font: 'Calibri' })],
          spacing:   { after: 0 },
        })],
      }),
    ]});
  });

  children.push(
    new Table({
      columnWidths: evCols,
      width: { size: CONTENT_WIDTH, type: WidthType.DXA },
      rows: [
        new TableRow({ children: ['File Name', 'Mapped Control', 'Date Added', 'Expiry Date', 'Status'].map((h, i) => headerCell(h, evCols[i])), tableHeader: true }),
        ...(evDataRows.length > 0 ? evDataRows : [
          new TableRow({ children: [new TableCell({
            columnSpan: 5,
            width: { size: CONTENT_WIDTH, type: WidthType.DXA },
            borders: CELL_BORDERS,
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: 'No evidence files uploaded.', size: 18, color: MUTED, italics: true, font: 'Calibri' })], spacing: { after: 0 } })],
          })] }),
        ]),
      ],
    }),
    blankLine(120),
    pageBreak(),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 10 — AUDIT TRAIL
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 7 \u2014 Audit Trail', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 160 } }),
    para('Last 50 control changes', { size: 16, color: MUTED, italic: true, spacingAfter: 200 }),
  );

  const histSlice = (controlHistory || []).slice(0, 50);

  if (histSlice.length === 0) {
    children.push(para('No control changes have been recorded in the audit trail.', { size: 18, color: MUTED, italic: true }));
  } else {
    // 5 cols: 1800+1440+1440+1440+3240 = 9360
    const auditCols = [1800, 1440, 1440, 1440, 3240];

    const auditDataRows = histSlice.map((h, i) => {
      const dateStr = h.created_at
        ? new Date(h.created_at).toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' })
        : '—';
      const oldLbl = h.old_status === 'yes' ? 'Implemented' : h.old_status === 'no' ? 'Not Implemented' : h.old_status === 'partial' ? 'Partial' : '—';
      const newLbl = h.new_status === 'yes' ? 'Implemented' : h.new_status === 'no' ? 'Not Implemented' : h.new_status === 'partial' ? 'Partial' : '—';
      const rowBg  = altBg(i);
      const { bg: oldBg, text: oldFg } = statusColors(oldLbl);
      const { bg: newBg, text: newFg } = statusColors(newLbl);

      return new TableRow({ children: [
        styledCell(dateStr,             { color: MUTED,    bgColor: rowBg, width: auditCols[0], fontSize: 16 }),
        styledCell(h.control_id || '—', { bold: true, color: NAVY, bgColor: rowBg, width: auditCols[1], fontSize: 16 }),
        new TableCell({ width: { size: auditCols[2], type: WidthType.DXA }, shading: { fill: oldBg, type: ShadingType.CLEAR, color: 'auto' }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, borders: CELL_BORDERS, children: [new Paragraph({ children: [new TextRun({ text: oldLbl, size: 16, color: oldFg, font: 'Calibri' })], spacing: { after: 0 } })] }),
        new TableCell({ width: { size: auditCols[3], type: WidthType.DXA }, shading: { fill: newBg, type: ShadingType.CLEAR, color: 'auto' }, margins: { top: 80, bottom: 80, left: 120, right: 120 }, borders: CELL_BORDERS, children: [new Paragraph({ children: [new TextRun({ text: newLbl, bold: true, size: 16, color: newFg, font: 'Calibri' })], spacing: { after: 0 } })] }),
        styledCell(h.changed_by_name || '—', { color: DARK_TEXT, bgColor: rowBg, width: auditCols[4], fontSize: 16 }),
      ]});
    });

    children.push(
      new Table({
        columnWidths: [1800, 1440, 1440, 1440, 3240],
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        rows: [
          new TableRow({ children: ['Date', 'Control ID', 'Previous Status', 'New Status', 'Changed By'].map((h, i) => headerCell(h, [1800, 1440, 1440, 1440, 3240][i])), tableHeader: true }),
          ...auditDataRows,
        ],
      }),
    );
  }

  children.push(blankLine(120), pageBreak());

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 11 — SPRS SCORE HISTORY (CMMC / NIST 171 only)
  // ════════════════════════════════════════════════════════════════════════════

  if (isCmmc) {
    children.push(
      new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Section 8 \u2014 SPRS Score History', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 200 } }),
    );

    const sprsSlice = (sprsHistory || []);
    if (sprsSlice.length < 2) {
      children.push(para('Insufficient score history. SPRS scores are automatically recorded each time you complete an assessment.', { size: 18, color: MUTED, italic: true }));
    } else {
      const first   = sprsSlice[0].score;
      const latest  = sprsSlice[sprsSlice.length - 1].score;
      const delta   = latest - first;
      const deltaStr = delta > 0 ? `+${delta}` : String(delta);

      children.push(para(`Current score: ${latest}  |  Starting score: ${first}  |  Change: ${deltaStr}`, { size: 16, color: MUTED, spacingAfter: 240 }));

      // 3 cols: 3120+3120+3120 = 9360
      const sprsCols = [3120, 3120, 3120];

      children.push(
        new Table({
          columnWidths: sprsCols,
          width: { size: CONTENT_WIDTH, type: WidthType.DXA },
          rows: [
            new TableRow({ children: ['Date Recorded', 'SPRS Score', 'Notes'].map((h, i) => headerCell(h, sprsCols[i])), tableHeader: true }),
            ...sprsSlice.map((s, i) => {
              const dateStr = s.recorded_at
                ? new Date(s.recorded_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                : '—';
              const scoreColor = (s.score || 0) >= 0 ? '065F46' : '991B1B';
              const rowBg = altBg(i);
              return new TableRow({ children: [
                styledCell(dateStr,          { color: MUTED,      bgColor: rowBg, width: sprsCols[0], fontSize: 18 }),
                styledCell(String(s.score ?? '—'), { bold: true, color: scoreColor, bgColor: rowBg, width: sprsCols[1], fontSize: 18 }),
                styledCell(trunc(s.notes || '', 60), { color: DARK_TEXT, bgColor: rowBg, width: sprsCols[2], fontSize: 18 }),
              ]});
            }),
          ],
        }),
      );
    }
    children.push(blankLine(120), pageBreak());
  }

  // ════════════════════════════════════════════════════════════════════════════
  // SECTION 12 — SIGNATURE BLOCK
  // ════════════════════════════════════════════════════════════════════════════

  children.push(
    new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: 'Signatures and Certification', bold: true, size: 28, color: NAVY, font: 'Calibri' })], spacing: { before: 0, after: 200 } }),
    para(
      'By signing below, the undersigned certify that the information contained in this System Security Plan is accurate and complete to the best of their knowledge, and that the described security controls are implemented or planned as documented herein.',
      { size: 18, align: AlignmentType.BOTH, spacingAfter: 400 },
    ),
  );

  const sigBlocks = [
    { role: 'System Owner',                               name: company?.system_owner_name    || '___________________________', titleLabel: 'Title', titleVal: company?.system_owner_title || '___________________________' },
    { role: 'Authorizing Official',                       name: company?.authorizing_official || '___________________________', titleLabel: 'Title', titleVal: '___________________________' },
    { role: 'Information System Security Officer (ISSO)', name: company?.isso_name            || '___________________________', titleLabel: 'Email',  titleVal: company?.isso_email || '___________________________' },
  ];

  // 3 cols each: let's use 3 equal columns with label + value layout
  // Each signature table: 3 cols = 2160 + 4320 + 2880 = 9360
  const sigCols = [2160, 4320, 2880];

  sigBlocks.forEach(sig => {
    children.push(
      new Table({
        columnWidths: sigCols,
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        rows: [
          // Header row spanning all 3 cols
          new TableRow({
            children: [new TableCell({
              columnSpan: 3,
              width: { size: CONTENT_WIDTH, type: WidthType.DXA },
              shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
              margins: { top: 100, bottom: 100, left: 180, right: 180 },
              borders: CELL_BORDERS,
              children: [new Paragraph({ children: [new TextRun({ text: sig.role, bold: true, size: 20, color: WHITE, font: 'Calibri' })], spacing: { after: 0 } })],
            })],
          }),
          // Name row
          new TableRow({ children: [
            styledCell('Name:',        { bold: true, color: NAVY, bgColor: LIGHT_GRAY, width: sigCols[0], fontSize: 18 }),
            styledCell(sig.name,       { color: DARK_TEXT, bgColor: WHITE, width: sigCols[1], fontSize: 18 }),
            styledCell('',             { bgColor: WHITE, width: sigCols[2], fontSize: 18 }),
          ]}),
          // Title/Email row
          new TableRow({ children: [
            styledCell(`${sig.titleLabel}:`, { bold: true, color: NAVY, bgColor: LIGHT_GRAY, width: sigCols[0], fontSize: 18 }),
            styledCell(sig.titleVal,   { color: DARK_TEXT, bgColor: WHITE, width: sigCols[1], fontSize: 18 }),
            styledCell('',             { bgColor: WHITE, width: sigCols[2], fontSize: 18 }),
          ]}),
          // Signature row
          new TableRow({ children: [
            styledCell('Signature:',   { bold: true, color: NAVY, bgColor: LIGHT_GRAY, width: sigCols[0], fontSize: 18 }),
            styledCell('___________________________________________________', { color: DARK_TEXT, bgColor: WHITE, width: sigCols[1], fontSize: 18 }),
            styledCell('Date: __________________', { color: MUTED, bgColor: WHITE, width: sigCols[2], fontSize: 18 }),
          ]}),
        ],
      }),
      blankLine(480),
    );
  });

  // Footer
  children.push(
    dividerLine(NAVY),
    para(`Document generated by TheClearAudit  |  ${orgName}  |  ${today}  |  ${meta.shortName}`, { size: 16, color: MUTED, align: AlignmentType.CENTER }),
  );

  // ════════════════════════════════════════════════════════════════════════════
  // BUILD DOCUMENT
  // ════════════════════════════════════════════════════════════════════════════
  const doc = new Document({
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 20 } },
      },
      paragraphStyles: [
        {
          id: 'Heading1',
          name: 'Heading 1',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { size: 28, bold: true, font: 'Calibri', color: NAVY },
          paragraph: { spacing: { before: 360, after: 180 }, outlineLevel: 0 },
        },
        {
          id: 'Heading2',
          name: 'Heading 2',
          basedOn: 'Normal',
          next: 'Normal',
          quickFormat: true,
          run: { size: 24, bold: true, font: 'Calibri', color: ACCENT },
          paragraph: { spacing: { before: 240, after: 120 }, outlineLevel: 1 },
        },
      ],
    },
    sections: [{
      properties: {
        page: {
          size:   { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const safeName = (company?.name || profile?.company_name || 'Organization')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');
  const dateStr = new Date().toISOString().split('T')[0];
  saveAs(blob, `TheClearAudit_SSP_${safeName}_${dateStr}.docx`);
}
